import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { AnalyticsBloc } from '@kanalytics';
import { EnvironmentVariablesService } from '@kenv';
import { AuthenticationBloc } from '@kp/auth/authentication.bloc';
import { GroupValidationBloc } from '@kp/auth/group-validation.bloc';
import { OnboardingBloc } from '@kp/onboarding/onboarding.bloc';
import { UserBloc } from '@kp/user/user.bloc';
import { BrowserStorage, DataStoreService, RouterHelperService } from '@kservice';
import { Product, UserType } from '@ktypes/enums';
import { OnboardingData } from '@ktypes/models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, combineLatest } from 'rxjs';
import { filter, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class NotAuthenticatedGuard {
  constructor(
    private _analyticsBloc: AnalyticsBloc,
    private authBloc: AuthenticationBloc,
    private browserStorage: BrowserStorage,
    private _dataStoreService: DataStoreService,
    private _deviceDetectorService: DeviceDetectorService,
    private environmentVariablesService: EnvironmentVariablesService,
    private onboardingBloc: OnboardingBloc,
    private groupValidationBloc: GroupValidationBloc,
    public router: Router,
    private routerHelper: RouterHelperService,
    private _userBloc: UserBloc
  ) {}

  canActivate(route: ActivatedRouteSnapshot /*state: RouterStateSnapshot*/): boolean | Observable<boolean | UrlTree> {
    const queryParams = (route?.queryParams ?? {}) as Record<string, unknown>;
    const onboardingData: OnboardingData = this.browserStorage.getObject('onboarding') as OnboardingData;
    const groupCode =
      (typeof queryParams['groupCode'] === 'string' ? queryParams['groupCode'] : undefined) ||
      (route?.params?.groupCode as string);
    const skipLandingRaw = queryParams['skip_landing'];
    const skipLanding = typeof skipLandingRaw === 'string' ? skipLandingRaw : undefined;
    const dataToken = typeof queryParams['dataToken'] === 'string' ? queryParams['dataToken'] : undefined;
    const source = typeof queryParams['source'] === 'string' ? queryParams['source'] : undefined;

    // if there is a source or dataToken query param, navigation will be handled with other guards, exit
    if (source || dataToken) {
      return false;
    }

    // Capture first-touch attribution immediately on entry, before we redirect and drop query params.
    this.onboardingBloc.captureAttributionFromQueryParams(queryParams);

    return combineLatest([
      this._userBloc.fetchingUser$,
      this._dataStoreService.user$,
      this._dataStoreService.authData$,
    ]).pipe(
      filter(
        ([fetchingUser, user]) => !fetchingUser?.data && ((!user || user?.hasLoadedData || user?.hasNoData) as boolean)
      ),
      map(([_, user, authData]) => {
        // if group code and no onboarding data (or Resourceful), validate group code and go to landing
        if (
          groupCode &&
          (!onboardingData ||
            onboardingData.groupCode !== groupCode ||
            this.environmentVariablesService.product === Product.resourceful)
        ) {
          if (user?.type !== UserType.user) {
            this.groupValidationBloc.startValidatingGroupCode(groupCode);
          }
          if (skipLanding != null) {
            void this.router.navigate(['/welcome'], {
              replaceUrl: true,
              queryParams: { skip_landing: skipLanding },
            });
          } else {
            void this.router.navigate(['/welcome'], { replaceUrl: true });
          }
          return false;
        } else if (
          groupCode &&
          onboardingData &&
          onboardingData.groupCode === groupCode &&
          this.environmentVariablesService.product === Product.purposeful
        ) {
          // if group code and onboarding data, restore onboarding position
          this.onboardingBloc.restoreSavedOnboardingValues(onboardingData);
          const latestPage = this._getOnboardingResumePage(onboardingData.latestPage);
          void this.router.navigate([`${latestPage?.startsWith?.('/') ? '' : '/'}${latestPage}`], { replaceUrl: true });
          return false;
        } else if (authData?.token && user?.type === UserType.user) {
          this._analyticsBloc.userReadyForAnalytics();
          const isRouteValid = this.routerHelper.isRouteValid(
            route.url
              ?.filter((url) => url.path !== 'initial')
              .map((url) => url.path)
              .join('/')
          );
          return this.router.parseUrl(!isRouteValid ? '/error' : '');
        }

        // TODO: ensure this check tries to refresh the authData if expired
        // TODO: maintain path for proper redirect after login?
        // otherwise, just allow the non-authenticated user to continue on
        if (
          !authData ||
          (!authData.token && (!user || !(user.type === UserType.user))) ||
          this.authBloc.isTokenExpiredLocally()
        ) {
          return true;
        }
        // fallback, just resend to root path and following routing rules from there
        return this.router.parseUrl('');
      })
    );
  }

  private _getOnboardingResumePage(latestPage: string) {
    if (latestPage?.startsWith('/welcome/eligibility')) {
      return this._deviceDetectorService.isDesktop()
        ? '/welcome/mobile-app/interstitial'
        : '/welcome/mobile-app/interstitial/mobile';
    }
    return latestPage;
  }
}
